<template>
    <b-modal
        id="modalAddAttachment"
        ref="modalAddAttachment"
        :lazy="true"
        title-tag="h2"
        :title="params.action === 'edit' ? 'Edit Attachment' : 'New Attachment'"
        :visible="isOpen"
        @close="close"
        @hidden="close"
        @shown="onOpen"
    >
        <b-form>
            <b-form-group
                label="Title: "
                class
                label-cols="3"
                label-for="title"
                v-if="params.action === 'edit'"
            >
                <input
                    id="title"
                    v-model="data.title"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('title') }"
                    class="form-control"
                    placeholder="Title of Attachment (optional)"
                    rows="3"
                    name="title"
                />
            </b-form-group>
            <Attachment
                v-if="params.action !== 'edit'"
                ref="attachment"
                :templateOptions="attachmentTemplateOptions"
                :label-cols="3"
                @onFileChange="onFileChange"
                @on-source-change="onFileChange"
            ></Attachment>
            <b-form-group
                label="Summary: "
                class
                label-cols="3"
                label-for="summary"
            >
                <b-form-textarea
                    id="summary"
                    v-model="data.summary"
                    placeholder="Summary of Attachment (optional)"
                    rows="3"
                ></b-form-textarea>
            </b-form-group>
            <b-form-group
                v-if="
                    attachmentSource != ATTACHMENT_OPTION.ATTACHMENT_LIFELEARN
                "
                label="Reference:"
                class
                label-cols="3"
                label-for="source"
            >
                <vet-select
                    id="source_id"
                    v-model="data.source"
                    :show-menu="false"
                    placeholder="Name of Client or Contact (optional)"
                    name="source_id"
                    module="client-all-contacts"
                    hide-suffix
                />
            </b-form-group>
        </b-form>
        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!loading"
                @click="$refs.modalAddAttachment.hide('cancel')"
                class="float-left"
                variant="secondary"
                >Cancel</b-button
            >
            <div class="float-right" v-if="!loading">
                <b-button
                    @click="save"
                    class="float-left"
                    variant="primary"
                    :disabled="!attachmentHasFile"
                    >Save</b-button
                >
            </div>
            <div class="float-right d-flex align-items-center" v-if="loading">
                <loader v-if="loading" :margin="2" />
            </div>
        </div>
    </b-modal>
</template>
<style></style>
<script>
import vetSelect from "@/module/commons/vet-select.vue";
import Attachment from "@/module/commons/attachment";
import Loader from "@/components/elements/loader";

export default {
    name: "ModalAddAttachment",
    components: {
        vetSelect,
        Attachment,
        Loader
    },
    props: {
        patientId: {
            type: String
        },
        labId: {
            type: String
        }
    },
    data() {
        return {
            attachmentHasFile: false,
            loading: false,
            attachmentTemplateOptions: [],
            attachmentSource: 0,
            data: {
                title: "",
                file: "",
                summary: "",
                source: null
            }
        };
    },
    computed: {
        isOpen() {
            return this.$store.state.patient.modalNewAttachment.isOpen;
        },
        params() {
            return this.$store.state.patient.modalNewAttachment; // return object about this modal
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        soapDetails() {
            if (this.isSoap) {
                return this.$store.getters["soap/soapDetails"];
            } else {
                return null;
            }
        }
    },
    methods: {
        onOpen() {
            this.getAttachmentTemplates();
            if (this.params.action === "edit") {
                this.data.title = this.params.attachment.title;
                this.data.summary = this.params.attachment.summary;
                if (this.params.attachment.source_id) {
                    this.data.source = {
                        id: this.params.attachment.source_id,
                        label: this.params.attachment.source_name,
                        source_type: this.params.attachment.source_type
                    };
                }

                if (
                    this.params.attachment.source_type ==
                    this.ATTACHMENT_SOURCE_TYPE.SOURCE_TYPE_LIFE_LEARN
                ) {
                    this.attachmentSource = this.ATTACHMENT_OPTION.ATTACHMENT_LIFELEARN;
                    this.data.source = {
                        source_type: this.ATTACHMENT_SOURCE_TYPE
                            .SOURCE_TYPE_LIFE_LEARN
                    };
                }

                if (this.params.attachment.file_uri) {
                    this.attachmentHasFile = true;
                }
            }
        },
        close() {
            this.$store.commit("patient/closeModalNewAttachment");
        },
        async save() {
            this.loading = true;

            // add
            if (this.params.action !== "edit") {
                let details = {
                    module: this.MODULE.PATIENT,
                    patient_id: this.patientId,
                    lab_id: this.params.record_id,
                    soap_id: this.isSoap
                        ? this.soapDetails.id
                        : this.params.soap_id,
                    summary: this.data.summary,
                    source_id: this.data.source ? this.data.source.id : null,
                    source_type: this.data.source
                        ? this.data.source.source_type
                        : null
                };

                const response = await this.$refs["attachment"].getFile(
                    details
                );

                if (response) {
                    this.$refs.modalAddAttachment.hide();
                    this._message("File attached");
                    this.$emit("reload");
                }
            }
            // edit
            else {
                let params = {
                    title: this.data.title,
                    summary: this.data.summary,
                    source_id: this.data.source ? this.data.source.id : null,
                    source_type: this.data.source
                        ? this.data.source.source_type
                        : null
                };

                const response = await this.$http.put(
                    process.env.BARRAMUNDI +
                        "record/attachment/" +
                        this.params.attachment.id,
                    params
                );

                if (response.data.response.message.code == 200) {
                    this.loading = false;
                    this.$refs.modalAddAttachment.hide();
                    this.$emit("reload");
                    this._message("Edits saved", "success");
                } else {
                    this.loading = false;
                    this._message(response.message, "danger");
                }
            }
        },
        onFileChange({ source, hasFile }) {
            this.attachmentHasFile = hasFile;
            this.attachmentSource = source;

            if (
                this.attachmentSource ==
                this.ATTACHMENT_OPTION.ATTACHMENT_LIFELEARN
            ) {
                this.data.source = {
                    id: null,
                    source_type: this.ATTACHMENT_SOURCE_TYPE
                        .SOURCE_TYPE_LIFE_LEARN
                };
            } else {
                this.data.source = null;
            }
        },
        async getAttachmentTemplates() {
            let self = this;
            await this.$http
                .get(process.env.BARRACUDA + "account/template/attachment")
                .then(function (response) {
                    if (response.data.response) {
                        if (response.data.response.message.code == 200) {
                            self.attachmentTemplateOptions =
                                response.data.response.resources;
                        }
                    }
                });
        }
    }
};
</script>
